import { slugify } from '@tracsis/library/lib/helpers';
import React, { useState } from 'react';

import {
	Button,
	ButtonGroup,
	Columns,
	Content,
	Grid,
	Image,
	Input,
	Select,
	TextArea,
} from '../../index';
import * as styles from './Form.module.css';
import H from '../Typography/H';
import { Tick } from '../Icon';
import Checkbox from './Input/Checkbox';

const Form = ({
	name,
	type,
	newEvent,
	select,
	selectTitle = 'Service category',
	selectNotifications = false,
	buttonTheme = 'reversed',
	lang = 'en-GB',
	title,
	singleForm
}: {
	name: string;
	type: string;
	newEvent?: boolean;
	select?: { value: string; label: string }[];
	selectTitle?: string;
	selectNotifications?: boolean;
	buttonTheme?: 'inverted' | 'ghost' | 'inline' | 'reversed' | undefined;
	lang?: string;
	title?: string;
	singleForm?: boolean
}) => {
	const [formSent, setFormSent] = useState(false);
	const [formError, setFormError] = useState(false);
	const [formData, setFormData] = useState({});
	const [notification, setNotification] = useState(
		(select && select[0].value) || null
	);

	const encode = (data: any) => {
		return Object.keys(data)
			.map(
				(key: string) =>
					encodeURIComponent(key) + '=' + encodeURIComponent(data[key])
			)
			.join('&');
	};

	const handleSubmit = (e: any) => {
		e.preventDefault();

		// Append the "select" value if multiple/different notifications are required
		const name = (notification && !singleForm)
			? `${e.target.getAttribute('name')}-${notification}`
			: e.target.getAttribute('name');

		fetch('/', {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: encode({
				'form-name': name,
				...formData,
			}),
		})
			.then((res) => {
				if (res.status === 200) {
					setFormSent(true);
					setFormError(false);
					setFormData({});
				} else {
					setFormError(true);
				}
			})
			.catch((error) => alert(error));
	};

	const handleChange = (e: any) => {
		const name = e.target.name;
		let value;

		if (name === `${slugify(selectTitle, "")}`) setNotification(e.target.value);

		switch (e.target.type) {
			case 'checkbox':
				value = e.target.checked ? e.target.checked : false;
				break;

			default:
				value = e.target.value;
				break;
		}

		setFormData({ ...formData, [name]: value });
	};

	const formCore = (
		<>
			<p hidden>
				<input
					name="sherwood"
					placeholder="Do not fill me out"
					onChange={(e: any) => handleChange(e)}
				/>
			</p>

			<h2>{title ? title : lang === 'en-US' ? 'Make an inquiry' : 'Make an enquiry'}</h2>

			<Input
				label="Name"
				placeholder="Name"
				name="name"
				handleChange={(e: any) => handleChange(e)}
			/>

			<Input
				label="Company name"
				placeholder="Company"
				name="company-name"
				handleChange={(e: any) => handleChange(e)}
			/>

			{newEvent && (
				<Input
					label="Company registration number"
					placeholder="Registration number"
					name="company-registration-number"
					handleChange={(e: any) => handleChange(e)}
				/>
			)}

			<Input
				label="E-mail address (required)"
				placeholder="Email"
				name="input"
				type="email"
				required
				handleChange={(e: any) => handleChange(e)}
			/>

			<Input
				label="Telephone number"
				placeholder="Telephone"
				name="telephone-number"
				handleChange={(e: any) => handleChange(e)}
			/>

			{select && (
				<Select
					label={`${selectTitle} *`}
					name={`${slugify(selectTitle, "-")}`}
					required
					handleChange={(e: any) => handleChange(e)}>
					<option hidden disabled selected value="">
						-- Select an option --
					</option>
					{select.map(({ value, label }, i: number) => (
						<option key={i} value={value}>{label}</option>
					))}
				</Select>
			)}

			{!newEvent && <TextArea
				label={lang === 'en-US' ? 'Your inquiry (required)' : 'Your enquiry (required)'}
				placeholder={lang === 'en-US' ? 'Inquiry' : 'Enquiry'}
				name={lang === 'en-US' ? 'inquiry' : 'enquiry'}
				required
				handleChange={(e: any) => handleChange(e)}
			/>}

			{newEvent && (
				<>
					<Input
						label="What is the name of the event?"
						placeholder="Event name"
						name="event-name"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Select
						label="Please select the type of event:"
						name="type-of-event"
						required
						handleChange={(e: any) => handleChange(e)}>
						<option hidden disabled selected value="">-- Select an option --</option>
						<option value="festival-or-greenfield-site">Festival/Greenfield site</option>
						<option value="venue">Venue</option>
						<option value="show">Show</option>
						<option value="enquine-event">Enquine event</option>
						<option value="motorsport">Motorsport</option>
						<option value="sports-event">Sports event</option>
						<option value="retail">Retail</option>
						<option value="mass-participation-event">Mass participation event</option>
						<option value="concert">Concert</option>
						<option value="golf-event">Golf event</option>
						<option value="agricultural-event">Agricultural event</option>
					</Select>

					<Input
						label="If the type of event is not listed, please specify the event type below:"
						placeholder="Custom event type"
						name="custom-event-type"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Input
						label="Where is the town or postcode where the event will take place?"
						placeholder="Location of event"
						name="event-location"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Input
						label="When is the event due to take place?"
						placeholder="Date of event"
						name="event-date"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Checkbox
						title="What services are required at this event?"
						items={[
							{
								label: "Planning and consultancy",
								value: "planning-and-consultancy",
								name: "event-required-services",
							},
							{
								label: "Car parking",
								value: "car-parking",
								name: "event-required-services",
							},
							{
								label: "Admission services",
								value: "admission-services",
								name: "event-required-services",
							},
							{
								label: "CSAS PATOS (Police-accredited traffic officers)",
								value: "CSAS-PATOS",
								name: "event-required-services",
							},
							{
								label: "Traffic management and/or signage",
								value: "traffic-management-and-or-signage",
								name: "event-required-services",
							},
							{
								label: "Vehicle counting / ANPR / Other technology",
								value: "vehicle-counting-or-equivalent",
								name: "event-required-services",
							},
						]}
					/>

					<Input
						label="Are there any additional services required at this event?"
						placeholder="Custom services"
						name="custom-services"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Input
						label="What is the expected attendance?"
						placeholder="Attendance of event"
						name="event-attendance"
						handleChange={(e: any) => handleChange(e)}
					/>

					<Checkbox
						title="How did you find out about Tracsis events?"
						items={[
							{
								label: "Search engine",
								value: "search-engine",
								name: "how-did-you-find-out-about-tracsis-events",
							},
							{
								label: "Social media",
								value: "social-media",
								name: "how-did-you-find-out-about-tracsis-events",
							},
							{
								label: "Advertisement",
								value: "advertisement",
								name: "how-did-you-find-out-about-tracsis-events",
							},
							{
								label: "Word of mouth",
								value: "word-of-mouth",
								name: "how-did-you-find-out-about-tracsis-events",
							},
							{
								label: "Recommendation",
								value: "recommendation",
								name: "how-did-you-find-out-about-tracsis-events",
							},
							{
								label: "Previous relationship",
								value: "previous-relationship",
								name: "how-did-you-find-out-about-tracsis-events",
							},
						]}
					/>

					<Select
						label="Do you have a current or previous contractor in place?"
						name="current-or-previous-contractor-in-place"
						required
						handleChange={(e: any) => handleChange(e)}>
						<option hidden disabled selected value="">-- Select an option --</option>
						<option value="yes">Yes</option>
						<option value="no">No</option>
					</Select>
				</>
			)}

			<ButtonGroup align="center">
				<Button>
					{lang === 'en-US' ? 'Submit inquiry' : 'Submit enquiry'}
				</Button>
			</ButtonGroup>
		</>
	);

	return formSent ? (
		<div className={styles.response}>
			<Grid>
				<Content>
					<H level={3}>
						Message received
					</H>
					<p>
						Thank you for your {lang === 'en-US' ? 'inquiry' : 'enquiry'}.
						We’ll process your request and be in touch as soon as possible.
					</p>

					<Button to={"/"} theme="reversed">
						Go to home page
					</Button>
				</Content>
				<div className={styles.checkmark}>
					<div className={styles.tickWrapper}>
						<Tick />
					</div>
				</div>
			</Grid>
		</div >
	) : (
		<>
			<form
				data-netlify="true"
				data-netlify-honeypot="sherwood"
				name={name}
				className={styles.form}
				encType={type}
				onSubmit={(e) => handleSubmit(e)}>
				{/* The `form-name` hidden field is required to support form submissions without JavaScript */}
				<input type="hidden" name="form-name" value={name} />
				{formCore}

				{formError && <p className='error'>There was an error in sending your submissions. Please try again.</p>}
			</form>

			{selectNotifications &&
				select &&
				select.map(({ value }, i: number) => {
					const formName = selectNotifications ? `${name}-${value}` : name;

					return (
						<form
							key={i}
							data-netlify="true"
							data-netlify-honeypot="sherwood"
							name={formName}
							className={styles.form}
							encType={type}
							hidden={true}>
							{/* The `form-name` hidden field is required to support form submissions without JavaScript */}
							<input type="hidden" name="form-name" value={formName} />
							{formCore}
						</form>
					);
				})
			}
		</>
	);
};

export default Form;
